<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/api';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import AdminBaseTable, { Field } from '@/components/admin/admin-base-table.vue';
import SharepointCredentialEditModal from '@/components/admin/user-documents/sharepoint-credential-edit-modal.vue';
import SharepointSiteEditModal from '@/components/admin/user-documents/sharepoint-site-edit-modal.vue';
import ActionBalloon from '@/components/common/molecules/balloon.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import UserDocument from '@/components/layouts/user-document.vue';
import { formatDateForStorage } from '@/utils/formatters';
import { checkMoveToAdmin } from '@/utils/user';
import { useSharepoint } from '@/utils/user-document/sharepoint';
import {
  ACTION_BLOCKED_STATUSES,
  AdminTableItem,
  displayStorageStatus,
  displaySyncStatus,
  isConnectedFailed,
  isSynced,
  isSyncing,
  isUncertified,
  showDisconnectModal,
} from '@/utils/user-document/storage';
import { useStore } from '@/utils/vue';

const router = useRouter();
const store = useStore();
const { createSnackbar } = useSnackbar();
const {
  connect: connectSharepoint,
  disconnect: disconnectSharepoint,
  updateDetails: updateDetailsSharepoint,
} = useSharepoint();

const actionMenuItem = ref<AdminTableItem | undefined>(undefined);
const isSharepointCredentialEditModalOpen = ref(false);
const isSharepointSiteEditModalOpen = ref(false);
const isDisabledSharepointSubmit = ref(false);
const adminTableItem = ref<AdminTableItem>({
  id: 2,
  storageType: 'share_point',
  isLoaded: false,
  isConnected: false,
  syncStatus: '-',
  fileCount: '-',
  syncStartedAt: '-',
  syncEndedAt: '-',
  connectText: 'ストレージを認証',
  status: 'NOT_CONNECTED',
});

const fields = computed<Field<AdminTableItem>[]>(() => [
  {
    name: 'isConnected',
    displayName: 'ストレージ状況',
    isSortable: false,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'syncStatus',
    displayName: 'ステータス',
    isSortable: false,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'fileCount',
    displayName: '同期済みのファイル数',
    isSortable: false,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'syncStartedAt',
    displayName: '同期開始時刻',
    isSortable: false,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'syncEndedAt',
    displayName: '同期終了時刻',
    isSortable: false,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'actions',
    displayName: '',
    isSortable: false,
    type: 'string',
    fieldPosition: 'right',
    cellPosition: 'right',
  },
]);

// 連携状態を更新
const updateStorageStatus = () => {
  updateDetailsSharepoint(adminTableItem.value);
};

const disconnectHandler = (item: AdminTableItem) => {
  const action = disconnectSharepoint(item);
  showDisconnectModal(store, item, action);
};

const editSharepointCredentialHandler = async (item: AdminTableItem) => {
  isSharepointCredentialEditModalOpen.value = true;
  isDisabledSharepointSubmit.value = ACTION_BLOCKED_STATUSES.includes(
    item.status,
  );
};

const editSharepointSiteHandler = async () => {
  await router.push({ name: 'sharepointSites' });
};

const downloadSharepointCertificateHandler = async () => {
  try {
    const blob = await api.issueSharepointCertificate();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sharepoint-certificate.pem';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    createSnackbar({
      message: 'SharePoint証明書をダウンロードしました',
      type: 'success',
    });
  } catch (error) {
    createSnackbar({
      message: 'SharePoint証明書のダウンロードに失敗しました',
      type: 'error',
    });
    throw error;
  }

  actionMenuItem.value = undefined;
};

const actions: {
  name: string;
  func: (item: AdminTableItem) => void;
}[] = [
  {
    name: 'ストレージの認証解除',
    func: disconnectHandler,
  },
  {
    name: 'ストレージの認証情報編集',
    func: editSharepointCredentialHandler,
  },
  {
    name: 'サイトを編集',
    func: editSharepointSiteHandler,
  },
  {
    name: '証明書をダウンロード',
    func: downloadSharepointCertificateHandler,
  },
];

const clickMenu = (item: AdminTableItem) => {
  actionMenuItem.value =
    actionMenuItem.value?.id === item.id ? undefined : item;
};

const connectHandler = (item: AdminTableItem) => {
  connectSharepoint(item);
};

const uncertifiedActions: {
  name: string;
  func: (item: AdminTableItem) => void;
}[] = [
  {
    name: '証明書をダウンロード',
    func: downloadSharepointCertificateHandler,
  },
];

onMounted(async () => {
  await checkMoveToAdmin(router);
  const teamInfo = await api.fetchTeamInfo();

  if (!teamInfo.enable_user_document) {
    createSnackbar({
      message: '権限がありません',
      type: 'error',
    });
    await router.push({ name: 'anewsHome' });
  }

  updateStorageStatus();
});
</script>

<template>
  <UserDocument>
    <div class="c-title-bold--m">SharePoint</div>
    <div class="container">
      <AdminBaseTable
        :is-stricky-header="true"
        :fields="fields"
        :items="[adminTableItem]"
        action-width="240px"
      >
        <template #header-fileCount="{ item }">
          <div class="header-title-with-icon">
            <div>{{ item.displayName }}</div>
            <DgrIcon
              size="small"
              name="question-circle"
              v-tooltip.top="{
                content:
                  '連携中のファイルはPDF/Word/PowerPoint/Excelのみとなります',
                theme: 'menu',
              }"
            />
          </div>
        </template>
        <template #cell-isConnected="{ item }">
          <span v-text="displayStorageStatus(item)"></span>
        </template>
        <template #cell-syncStatus="{ item }">
          <DgrIcon
            :keep-fill="false"
            name="chart-round-bubble"
            v-if="isSyncing(item.status)"
            class="name-icon syncing"
          />
          <DgrIcon
            :keep-fill="false"
            name="check"
            v-else-if="isSynced(item.status)"
            class="name-icon synced"
          />
          <span v-text="displaySyncStatus(item)"></span>
        </template>
        <template #cell-fileCount="{ item }">
          <span v-text="item.fileCount"></span>
        </template>
        <template #cell-syncStartedAt="{ item }">
          <span v-text="formatDateForStorage(item.syncStartedAt)"></span>
        </template>
        <template #cell-syncEndedAt="{ item }">
          <span v-text="formatDateForStorage(item.syncEndedAt)"></span>
        </template>
        <template #cell-actions="{ item }">
          <div v-if="isUncertified(item)">
            <div class="action-connect actions">
              <div class="action-buttons">
                <button
                  class="action-button c-outlineBtn c-btn--auto c-btn--small c-outlineBtn--secondary"
                  :class="{ disabled: !item.isLoaded }"
                  :disabled="!item.isLoaded"
                  @click="connectHandler(item)"
                  v-text="item.connectText"
                ></button>
                <div>
                  <div class="action-icon" @click.stop="clickMenu(item)">
                    <DgrIcon name="ellipsis-h" />
                  </div>
                  <div class="action-menu">
                    <ActionBalloon
                      position="absolute"
                      :is-show="actionMenuItem?.id === item.id"
                      balloon-position="right"
                      @on-click-outside="actionMenuItem = undefined"
                    >
                      <div
                        v-for="action in uncertifiedActions"
                        :key="action.name"
                      >
                        <div
                          class="item c-text-regular--m"
                          @click="action.func(item)"
                        >
                          {{ action.name }}
                        </div>
                      </div>
                    </ActionBalloon>
                  </div>
                </div>
              </div>
              <div class="connect-failed" v-if="isConnectedFailed(item.status)">
                認証情報の取得に失敗しました。再度やり直してください。
              </div>
            </div>
          </div>
          <div v-else>
            <div class="actions">
              <div
                data-testid="action-icon"
                class="action-icon"
                @click.stop="clickMenu(item)"
              >
                <DgrIcon name="ellipsis-h" />
              </div>
              <div class="action-menu">
                <ActionBalloon
                  position="absolute"
                  :is-show="actionMenuItem?.id === item.id"
                  balloon-position="right"
                  @on-click-outside="actionMenuItem = undefined"
                >
                  <div v-for="action in actions" :key="action.name">
                    <div
                      class="item c-text-regular--m"
                      @click="
                        action.func ? action.func(item) : undefined;
                        actionMenuItem = undefined;
                      "
                    >
                      {{ action.name }}
                    </div>
                  </div>
                </ActionBalloon>
              </div>
            </div>
          </div>
        </template>
      </AdminBaseTable>
    </div>
    <SharepointCredentialEditModal
      v-model:open="isSharepointCredentialEditModalOpen"
      :is-disabled="isDisabledSharepointSubmit"
      @on-updated="updateStorageStatus"
    />
    <SharepointSiteEditModal
      v-model:open="isSharepointSiteEditModalOpen"
      :is-disabled="isDisabledSharepointSubmit"
      @on-updated="updateStorageStatus"
    />
  </UserDocument>
</template>

<style scoped lang="scss">
.container {
  margin-top: 16px;

  .header-title-with-icon {
    display: flex;
    gap: 8px;
    flex-wrap: nowrap;
    align-items: center;
  }

  .name-icon {
    display: inline-block;
    margin-right: 10px;
    vertical-align: middle;

    &.syncing {
      color: $color-gray800;
    }
    &.synced {
      color: $color-green600;
    }
    &.sync-failed {
      color: $color-orange1000;
    }
  }

  .action-icon {
    display: flex;
    justify-content: center;
    padding: 8px;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background-color: $color-gray200;
    }
  }

  .action-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: flex-end;
  }

  .action-button {
    white-space: nowrap;
    margin-left: auto;
  }

  .connect-failed {
    text-align: right;
    color: $color-orange1000;
  }

  .actions {
    display: inline-block;
  }

  .item {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 4px 12px;
    &:hover {
      background: $color-gray400;
    }
    &.disabled {
      color: $color-gray600;
    }
    .icon {
      margin-right: 4px;
    }
  }
}
</style>
